'use client'

import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { handleRegNumberValidation, handleSubmit } from '@components/common/AutoWidget/utils'
import { getConfig } from '@configs/envConfig'
import {
  autoWidgetJourney,
  enterJourneyEkind,
  enterJourneyEventName,
  newFlowJourney,
  newFlowJourneyEkind,
  newFlowJourneyEventName,
} from '@components/common/AutoWidget/config'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import { useUserContext } from '@context/UserContext/UserContenxt'
import { getPageType } from '@helpers/commonHelpers'
import { getR2d2CommonProperties, getR2d2Payload, refreshR2d2Cookies } from '@utils/r2d2Payload'
import { lobSegmentEventProperties, segmentEventCommonPropFunc, semAutoR2d2properties } from '@utils/segmentProperties'
import { isTurnstileEnabledForProduct, preloadTurnstile } from '@utils/turnstile/turnstile'
import { useEffect, useMemo, useRef, useState } from 'react'
import { AutoProduct } from 'src/enums/AutoProduct'
import { buildNewBikeRedirectionUrl } from './heroWidgetBehavior.mjs'

export const bikeProposalAPIUrl = '/motororchestrator/api/v2/proposals'
export const bikeProposalAPIResponseInitial = {
  error_context: '',
  error_message: '',
  error_code: '',
  request: { response: '' },
}

const fallbackRegexPatternsData = ['.*']

interface UseBikeRegistrationFlowArgs {
  ctaText: string
  initialRegistrationNumber?: string
  isSemPage?: boolean
  regexPatternsData?: string[]
  segmentHeading: string
}

export const useBikeRegistrationFlow = ({
  ctaText,
  initialRegistrationNumber = '',
  isSemPage = true,
  regexPatternsData = fallbackRegexPatternsData,
  segmentHeading,
}: UseBikeRegistrationFlowArgs) => {
  const [regNumber, setRegNumber] = useState(initialRegistrationNumber)
  const [isFetching, setIsFetching] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showIllogicalFlowModal, setShowIllogicalFlowModal] = useState(false)
  const [proposalAPIResponse, setProposalAPIResponse] = useState<any>(bikeProposalAPIResponseInitial)
  const [properties, setProperties] = useState({})
  const [continueInsuringSegmentEventProps, setContinueInsuringSegmentEventProps] = useState({})
  const lastTrackedErrorRef = useRef('')
  const { userData } = useUserContext()
  const envConfig = useEnvVariablesClientConfig()
  const product = AutoProduct.Bike
  const widgetType = getPageType(isSemPage)
  const effectiveRegexPatternsData = regexPatternsData?.length ? regexPatternsData : fallbackRegexPatternsData
  const registrationError = errors[product] || ''
  const turnstileConfig = getConfig(envConfig.ACKO_ENV)?.turnstile

  useEffect(() => {
    if (!isTurnstileEnabledForProduct(turnstileConfig, product)) return

    void preloadTurnstile(turnstileConfig || {}).catch(error => {
      console.error('Turnstile preload failed:', error)
    })
  }, [product, turnstileConfig])

  const segmentEventCommonProperties = useMemo(
    () => ({
      productWidgetData: {
        Content: {
          data: {
            attributes: {
              widget: 'bike-seo-widget',
              productWidgetMainHeading: segmentHeading,
            },
          },
        },
      },
    }),
    [segmentHeading]
  )

  const additionalProperties = useMemo(
    () => ({
      cta_text: ctaText,
      funnel: 'entry',
      communication_pincode: '',
      widget_type: 'purple_widget',
      journey: autoWidgetJourney[product][widgetType],
      ...(!isSemPage && { page_type: widgetType }),
    }),
    [ctaText, isSemPage, product, widgetType]
  )

  useEffect(() => {
    let isMounted = true
    const getProperties = async () => {
      try {
        const additionalLobEventProperties = (await lobSegmentEventProperties[product]?.(isSemPage)) || {}
        const props = await segmentEventCommonPropFunc(segmentEventCommonProperties)
        if (isMounted) setProperties({ ...props })
        if (isMounted) setContinueInsuringSegmentEventProps({ ...props, ...additionalLobEventProperties })
      } catch (error) {
        console.error('[lp/bike-insurance] Failed to build segment properties:', error)
      }
    }

    void getProperties()

    return () => {
      isMounted = false
    }
  }, [isSemPage, product, segmentEventCommonProperties])

  useEffect(() => {
    const hasError = Object.keys(errors).length > 0
    setShowError(hasError)

    if (!hasError) {
      lastTrackedErrorRef.current = ''
      return
    }

    const errorTrackingKey = `${registrationError}:${regNumber}`
    if (lastTrackedErrorRef.current === errorTrackingKey) return
    lastTrackedErrorRef.current = errorTrackingKey

    const trackError = async () => {
      try {
        await trackEvent('error_triggered_on_cta', {
          ...properties,
          ...additionalProperties,
          ...getR2d2Payload({
            isSemPage,
            ekind: 'invalid_reg_number',
            edata: { ...semAutoR2d2properties },
            odata: { product },
          }),
        })
      } catch (error) {
        console.error('[lp/bike-insurance] Failed to track registration error:', error)
      }
    }

    void trackError()
  }, [additionalProperties, errors, isSemPage, product, properties, regNumber, registrationError])

  const resetIllogicalFlow = () => {
    setShowIllogicalFlowModal(false)
    setProposalAPIResponse(bikeProposalAPIResponseInitial)
  }

  const handleRegistrationNumberChange = value => {
    const nextValue = typeof value === 'string' ? value : value?.target?.value || ''
    setRegNumber(nextValue.toUpperCase())

    if (Object.entries(errors).length > 0) {
      setErrors({})
      setShowError(false)
    }
  }

  const fetchAndInvokeHandleSubmit = async () => {
    await handleSubmit({
      proposalAPIUrl: bikeProposalAPIUrl,
      regNo: regNumber,
      userData,
      setIsFetching,
      setErrors,
      setProposalAPIResponse,
      setShowIllogicalFlowModal,
      InsuretrackEventName: enterJourneyEventName[product][widgetType],
      InsureSegmentTrackProps: {
        ...properties,
        ...additionalProperties,
        ...getR2d2Payload({
          isSemPage,
          ekind: enterJourneyEkind[product][widgetType],
          edata: {
            product_state: isSemPage ? 'Marketing Landing Pages' : null,
            screen: 'registration_number_screen',
            registration_number: regNumber,
          },
          odata: { product },
        }),
        ...(!isSemPage && { journey: 'fresh_bike', product }),
      },
      inputCtaText: ctaText,
      product,
      setShowError,
      regexPatternsData: effectiveRegexPatternsData,
      turnstileConfig,
    })
  }

  const handleRegistrationNumberBlur = async () => {
    try {
      await trackEvent('tap_registration_number_cta', {
        ...properties,
        ...additionalProperties,
        registration_number: regNumber,
        ...getR2d2Payload({
          isSemPage,
          ekind: 'tap_btn_enter_your_bike_number',
          edata: { ...semAutoR2d2properties },
          odata: { product },
        }),
      })
    } catch (error) {
      console.error('[lp/bike-insurance] Failed to track registration blur:', error)
    }

    if (regNumber) {
      handleRegNumberValidation(regNumber, setErrors, product, effectiveRegexPatternsData)
    }
  }

  const handleNewBikeClick = async () => {
    const segmentEventProps = {
      ...properties,
      ...additionalProperties,
      ...getR2d2Payload({
        ekind: newFlowJourneyEkind[product][widgetType],
        edata: { journey: 'new_bike', lob: 'direct', pa_cover: false },
        odata: { product },
        r2d2CommonProperties: getR2d2CommonProperties(),
      }),
      journey: isSemPage ? newFlowJourney[product][widgetType] : 'new_bike',
      ...(!isSemPage && { product }),
    }

    try {
      await trackEvent(newFlowJourneyEventName[product][widgetType], refreshR2d2Cookies(segmentEventProps))
    } catch (error) {
      console.error('[lp/bike-insurance] Failed to track new bike click:', error)
    }

    window.location.href = buildNewBikeRedirectionUrl(window.location.search)
  }

  return {
    continueInsuringSegmentEventProps,
    fetchAndInvokeHandleSubmit,
    handleNewBikeClick,
    handleRegistrationNumberBlur,
    handleRegistrationNumberChange,
    isFetching,
    product,
    proposalAPIResponse,
    regNumber,
    registrationError,
    resetIllogicalFlow,
    setProposalAPIResponse,
    setShowIllogicalFlowModal,
    showError,
    showIllogicalFlowModal,
  }
}
